<template>
  <div class="modal fade" id="resolveDisputeModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-check-circle-outline me-2"></i>
            Resolve Dispute - {{ disputeDetail.disputeId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Dispute Summary -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="alert alert-info">
                <h6 class="alert-heading mb-2">
                  <i class="mdi mdi-information me-2"></i>
                  Dispute Summary
                </h6>
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Customer:</strong> {{ disputeDetail.customerName }}</p>
                    <p class="mb-1"><strong>Type:</strong> {{ formatDisputeType(disputeDetail.disputeType) }}</p>
                    <p class="mb-0"><strong>Reference:</strong> {{ disputeDetail.referenceNo }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Original Amount:</strong> {{ formatPrice(disputeDetail.disputeAmount) }}</p>
                    <p class="mb-1"><strong>Priority:</strong>
                      <span class="badge" :class="getPriorityClass(disputeDetail.priority)">{{ disputeDetail.priority }}</span>
                    </p>
                    <p class="mb-0"><strong>Date Raised:</strong> {{ formatDate(disputeDetail.dateRaised) }}</p>
                  </div>
                  <div class="col-md-4">
                    <p class="mb-1"><strong>Days Open:</strong> {{ getDaysOpen(disputeDetail.dateRaised) }} days</p>
                    <p class="mb-1"><strong>Assigned To:</strong> {{ disputeDetail.assignedTo || 'Unassigned' }}</p>
                    <p class="mb-0"><strong>Current Status:</strong>
                      <span class="badge bg-warning">{{ formatStatus(disputeDetail.status) }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Resolution Form -->
          <form @submit.prevent="resolveDispute">
            <div class="row">
              <!-- Resolution Type -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Resolution Details</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Resolution Type <span class="text-danger">*</span></label>
                      <select v-model="resolutionForm.resolutionType" @change="handleResolutionTypeChange"
                              class="form-select" required>
                        <option value="">Select resolution type...</option>
                        <option value="BILLING_CORRECTION">Billing Correction</option>
                        <option value="PAYMENT_ADJUSTMENT">Payment Adjustment</option>
                        <option value="REFUND_ISSUED">Refund Issued</option>
                        <option value="WAIVER_APPLIED">Waiver Applied</option>
                        <option value="CUSTOMER_ERROR">Customer Error - No Action</option>
                        <option value="SYSTEM_UPDATE">System Update Required</option>
                        <option value="POLICY_CLARIFICATION">Policy Clarification</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>

                    <div v-if="resolutionForm.resolutionType === 'OTHER'" class="mb-3">
                      <label class="form-label">Specify Resolution Type <span class="text-danger">*</span></label>
                      <input v-model="resolutionForm.customResolutionType" type="text" class="form-control"
                             placeholder="Describe the resolution type" required>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Resolution Status <span class="text-danger">*</span></label>
                      <select v-model="resolutionForm.resolutionStatus" class="form-select" required>
                        <option value="RESOLVED_FAVOR_CUSTOMER">Resolved in Customer's Favor</option>
                        <option value="RESOLVED_FAVOR_COUNTY">Resolved in County's Favor</option>
                        <option value="RESOLVED_PARTIAL">Partially Resolved</option>
                        <option value="RESOLVED_COMPROMISE">Mutual Compromise</option>
                      </select>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Customer Satisfaction Rating</label>
                      <select v-model="resolutionForm.satisfactionRating" class="form-select">
                        <option value="">Not Rated</option>
                        <option value="5">Excellent (5/5)</option>
                        <option value="4">Good (4/5)</option>
                        <option value="3">Average (3/5)</option>
                        <option value="2">Poor (2/5)</option>
                        <option value="1">Very Poor (1/5)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Financial Adjustments -->
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Financial Impact</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <div class="form-check form-switch">
                        <input v-model="resolutionForm.hasFinancialImpact" class="form-check-input"
                               type="checkbox" id="financialImpact">
                        <label class="form-check-label fw-bold" for="financialImpact">
                          This resolution has financial impact
                        </label>
                      </div>
                    </div>

                    <div v-if="resolutionForm.hasFinancialImpact">
                      <div class="mb-3">
                        <label class="form-label">Adjustment Type</label>
                        <select v-model="resolutionForm.adjustmentType" class="form-select">
                          <option value="CREDIT">Credit to Customer</option>
                          <option value="REFUND">Cash Refund</option>
                          <option value="WAIVER">Waiver/Write-off</option>
                          <option value="CORRECTION">Bill Correction</option>
                          <option value="PENALTY_REVERSAL">Penalty Reversal</option>
                        </select>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Adjustment Amount (KES)</label>
                        <input v-model="resolutionForm.adjustmentAmount" type="number" step="0.01"
                               class="form-control" placeholder="0.00">
                      </div>

                      <div class="mb-3">
                        <label class="form-label">New Total Amount (KES)</label>
                        <input v-model="resolutionForm.newTotalAmount" type="number" step="0.01"
                               class="form-control" placeholder="0.00">
                        <small class="text-muted">Final amount after adjustments</small>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Approval Required</label>
                        <select v-model="resolutionForm.approvalLevel" class="form-select">
                          <option value="NONE">No Approval Required</option>
                          <option value="SUPERVISOR">Supervisor Approval</option>
                          <option value="MANAGER">Manager Approval</option>
                          <option value="DIRECTOR">Director Approval</option>
                        </select>
                      </div>
                    </div>

                    <!-- Financial Summary -->
                    <div class="alert alert-light">
                      <h6 class="mb-2">Financial Summary</h6>
                      <div class="row">
                        <div class="col-6">
                          <small class="text-muted">Original Amount:</small>
                          <p class="mb-1 fw-bold">{{ formatPrice(disputeDetail.disputeAmount) }}</p>
                        </div>
                        <div class="col-6">
                          <small class="text-muted">Adjustment:</small>
                          <p class="mb-1 fw-bold" :class="resolutionForm.adjustmentAmount < 0 ? 'text-danger' : 'text-success'">
                            {{ formatPrice(resolutionForm.adjustmentAmount || 0) }}
                          </p>
                        </div>
                      </div>
                      <hr class="my-2">
                      <div class="d-flex justify-content-between">
                        <strong>Final Amount:</strong>
                        <strong>{{ formatPrice(resolutionForm.newTotalAmount || disputeDetail.disputeAmount) }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Resolution Details -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Resolution Summary</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Resolution Summary <span class="text-danger">*</span></label>
                      <textarea v-model="resolutionForm.resolutionSummary" class="form-control" rows="3"
                                placeholder="Provide a brief summary of how the dispute was resolved..." required></textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Detailed Resolution Notes <span class="text-danger">*</span></label>
                      <textarea v-model="resolutionForm.resolutionNotes" class="form-control" rows="4"
                                placeholder="Provide detailed explanation of the investigation findings, actions taken, and resolution details..." required></textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Actions Taken</label>
                      <div class="form-check-group">
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="BILL_CORRECTED" id="action1">
                          <label class="form-check-label" for="action1">Bill Corrected</label>
                        </div>
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="PAYMENT_PROCESSED" id="action2">
                          <label class="form-check-label" for="action2">Payment Processed</label>
                        </div>
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="SYSTEM_UPDATED" id="action3">
                          <label class="form-check-label" for="action3">System Updated</label>
                        </div>
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="REFUND_ISSUED" id="action4">
                          <label class="form-check-label" for="action4">Refund Issued</label>
                        </div>
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="POLICY_UPDATED" id="action5">
                          <label class="form-check-label" for="action5">Policy Updated</label>
                        </div>
                        <div class="form-check">
                          <input v-model="resolutionForm.actionsTaken" class="form-check-input" type="checkbox"
                                 value="CUSTOMER_EDUCATED" id="action6">
                          <label class="form-check-label" for="action6">Customer Educated</label>
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Lessons Learned / Improvement Recommendations</label>
                      <textarea v-model="resolutionForm.lessonsLearned" class="form-control" rows="3"
                                placeholder="Any insights gained or recommendations for preventing similar disputes..."></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Customer Communication -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Customer Communication</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <div class="form-check form-switch">
                        <input v-model="resolutionForm.notifyCustomer" class="form-check-input"
                               type="checkbox" id="notifyCustomer" checked>
                        <label class="form-check-label fw-bold" for="notifyCustomer">
                          Notify customer of resolution
                        </label>
                      </div>
                    </div>

                    <div v-if="resolutionForm.notifyCustomer">
                      <div class="mb-3">
                        <label class="form-label">Notification Method</label>
                        <div class="form-check-group">
                          <div class="form-check form-check-inline">
                            <input v-model="resolutionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="EMAIL" id="notifyEmail">
                            <label class="form-check-label" for="notifyEmail">Email</label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input v-model="resolutionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="SMS" id="notifySMS">
                            <label class="form-check-label" for="notifySMS">SMS</label>
                          </div>
                          <div class="form-check form-check-inline">
                            <input v-model="resolutionForm.notificationMethods" class="form-check-input"
                                   type="checkbox" value="PHONE" id="notifyPhone">
                            <label class="form-check-label" for="notifyPhone">Phone Call</label>
                          </div>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Customer Message</label>
                        <textarea v-model="resolutionForm.customerMessage" class="form-control" rows="4"
                                  :placeholder="getDefaultCustomerMessage()"></textarea>
                        <small class="text-muted">This message will be sent to the customer</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Supporting Documents -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Supporting Documents</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Upload Resolution Documents</label>
                      <input @change="handleFileUpload" type="file" class="form-control"
                             multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                      <small class="text-muted">Upload corrected bills, approval documents, or other supporting files</small>
                    </div>

                    <!-- File preview -->
                    <div v-if="uploadedFiles.length > 0" class="border rounded p-3">
                      <h6 class="mb-2">Uploaded Documents:</h6>
                      <div v-for="(file, index) in uploadedFiles" :key="index"
                           class="d-flex justify-content-between align-items-center mb-2">
                        <div class="d-flex align-items-center">
                          <i :class="getFileIcon(file.name)" class="me-2"></i>
                          <span>{{ file.name }}</span>
                          <small class="text-muted ms-2">({{ formatFileSize(file.size) }})</small>
                        </div>
                        <button @click="removeFile(index)" type="button" class="btn btn-outline-danger btn-sm">
                          <i class="mdi mdi-close"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Submit Buttons -->
            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button v-if="needsApproval" type="button" @click="submitForApproval"
                      :disabled="!canSubmit || submitting" class="btn btn-warning">
                <span v-if="!submitting">
                  <i class="mdi mdi-account-check me-1"></i>
                  Submit for Approval
                </span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Submitting...
                </span>
              </button>
              <button v-else type="submit" :disabled="!canSubmit || submitting" class="btn btn-success">
                <span v-if="!submitting">
                  <i class="mdi mdi-check-circle me-1"></i>
                  Resolve Dispute
                </span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Resolving...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "ResolveDisputeModal",
  props: {
    disputeDetail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      submitting: false,
      uploadedFiles: [],
      resolutionForm: {
        resolutionType: '',
        customResolutionType: '',
        resolutionStatus: 'RESOLVED_FAVOR_CUSTOMER',
        satisfactionRating: '',
        hasFinancialImpact: false,
        adjustmentType: '',
        adjustmentAmount: 0,
        newTotalAmount: 0,
        approvalLevel: 'NONE',
        resolutionSummary: '',
        resolutionNotes: '',
        actionsTaken: [],
        lessonsLearned: '',
        notifyCustomer: true,
        notificationMethods: ['EMAIL'],
        customerMessage: ''
      }
    }
  },
  computed: {
    canSubmit() {
      return this.resolutionForm.resolutionType &&
          this.resolutionForm.resolutionSummary.trim() &&
          this.resolutionForm.resolutionNotes.trim();
    },

    needsApproval() {
      return this.resolutionForm.approvalLevel !== 'NONE' && this.resolutionForm.hasFinancialImpact;
    }
  },
  mounted() {
    this.resolutionForm.newTotalAmount = this.disputeDetail.disputeAmount;
    this.resolutionForm.customerMessage = this.getDefaultCustomerMessage();
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY');
    },

    formatDisputeType(type) {
      const types = {
        'BILLING_ERROR': 'Billing Error',
        'PAYMENT_NOT_REFLECTED': 'Payment Not Reflected',
        'OVERCHARGE': 'Overcharge',
        'DUPLICATE_CHARGE': 'Duplicate Charge',
        'REFUND_REQUEST': 'Refund Request',
        'OTHER': 'Other'
      };
      return types[type] || type;
    },

    formatStatus(status) {
      const statuses = {
        'PENDING': 'Pending',
        'UNDER_REVIEW': 'Under Review',
        'RESOLVED': 'Resolved',
        'REJECTED': 'Rejected',
        'ESCALATED': 'Escalated'
      };
      return statuses[status] || status;
    },

    getPriorityClass(priority) {
      switch (priority) {
        case 'LOW':
          return 'bg-light text-dark';
        case 'MEDIUM':
          return 'bg-info';
        case 'HIGH':
          return 'bg-warning';
        case 'URGENT':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getDaysOpen(dateString) {
      return moment().diff(moment(dateString), 'days');
    },

    handleResolutionTypeChange() {
      // Auto-set some defaults based on resolution type
      switch (this.resolutionForm.resolutionType) {
        case 'BILLING_CORRECTION':
          this.resolutionForm.hasFinancialImpact = true;
          this.resolutionForm.adjustmentType = 'CORRECTION';
          break;
        case 'REFUND_ISSUED':
          this.resolutionForm.hasFinancialImpact = true;
          this.resolutionForm.adjustmentType = 'REFUND';
          this.resolutionForm.approvalLevel = 'SUPERVISOR';
          break;
        case 'WAIVER_APPLIED':
          this.resolutionForm.hasFinancialImpact = true;
          this.resolutionForm.adjustmentType = 'WAIVER';
          this.resolutionForm.approvalLevel = 'MANAGER';
          break;
        case 'CUSTOMER_ERROR':
          this.resolutionForm.hasFinancialImpact = false;
          this.resolutionForm.resolutionStatus = 'RESOLVED_FAVOR_COUNTY';
          break;
      }

      // Update customer message
      this.resolutionForm.customerMessage = this.getDefaultCustomerMessage();
    },

    getDefaultCustomerMessage() {
      const customer = this.disputeDetail.customerName || 'Customer';
      const disputeId = this.disputeDetail.disputeId || '';

      switch (this.resolutionForm.resolutionType) {
        case 'BILLING_CORRECTION':
          return `Dear ${customer},\n\nYour dispute ${disputeId} has been resolved. We have corrected the billing error and adjusted your account accordingly.\n\nThank you for bringing this to our attention.\n\nBest regards,\nCustomer Service Team`;
        case 'REFUND_ISSUED':
          return `Dear ${customer},\n\nYour dispute ${disputeId} has been resolved in your favor. A refund has been processed and will reflect in your account within 3-5 business days.\n\nThank you for your patience.\n\nBest regards,\nCustomer Service Team`;
        case 'CUSTOMER_ERROR':
          return `Dear ${customer},\n\nRegarding your dispute ${disputeId}, our investigation shows that the charges are correct as per our policies. We have provided clarification on the billing structure.\n\nIf you have further questions, please contact us.\n\nBest regards,\nCustomer Service Team`;
        default:
          return `Dear ${customer},\n\nYour dispute ${disputeId} has been resolved. Please find the details of the resolution attached.\n\nThank you for your patience.\n\nBest regards,\nCustomer Service Team`;
      }
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        if (file.size <= 10 * 1024 * 1024) { // 10MB limit
          this.uploadedFiles.push(file);
        } else {
          alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        }
      });
      event.target.value = '';
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase();
      switch (extension) {
        case 'pdf':
          return 'mdi mdi-file-pdf-box text-danger';
        case 'doc':
        case 'docx':
          return 'mdi mdi-file-word-box text-primary';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'mdi mdi-file-image-box text-info';
        default:
          return 'mdi mdi-file-document-box text-secondary';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async resolveDispute() {
      try {
        this.submitting = true;

        // Validate form
        if (!this.validateResolution()) {
          this.submitting = false;
          return;
        }

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit resolution event
        this.$emit('dispute-resolved', this.disputeDetail.disputeId, this.resolutionForm);

        // Close modal
        this.closeModal();

        this.submitting = false;
      } catch (error) {
        this.submitting = false;
        alert('Error resolving dispute: ' + error.message);
      }
    },

    async submitForApproval() {
      try {
        this.submitting = true;

        // Validate form
        if (!this.validateResolution()) {
          this.submitting = false;
          return;
        }

        // Simulate API call for approval submission
        await new Promise(resolve => setTimeout(resolve, 2000));

        alert(`Resolution submitted for ${this.resolutionForm.approvalLevel.toLowerCase()} approval`);

        // Close modal
        this.closeModal();

        this.submitting = false;
      } catch (error) {
        this.submitting = false;
        alert('Error submitting for approval: ' + error.message);
      }
    },

    validateResolution() {
      if (!this.resolutionForm.resolutionType) {
        alert('Please select a resolution type');
        return false;
      }

      if (!this.resolutionForm.resolutionSummary.trim()) {
        alert('Please provide a resolution summary');
        return false;
      }

      if (!this.resolutionForm.resolutionNotes.trim()) {
        alert('Please provide detailed resolution notes');
        return false;
      }

      if (this.resolutionForm.hasFinancialImpact) {
        if (!this.resolutionForm.adjustmentType) {
          alert('Please select an adjustment type for financial impact');
          return false;
        }
        if (!this.resolutionForm.adjustmentAmount && this.resolutionForm.adjustmentAmount !== 0) {
          alert('Please specify the adjustment amount');
          return false;
        }
      }

      if (this.resolutionForm.notifyCustomer && this.resolutionForm.notificationMethods.length === 0) {
        alert('Please select at least one notification method');
        return false;
      }

      return true;
    },

    closeModal() {
      const modalElement = document.getElementById('resolveDisputeModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    }
  },
  watch: {
    // Update new total when adjustment amount changes
    'resolutionForm.adjustmentAmount'(newAmount) {
      if (this.resolutionForm.hasFinancialImpact) {
        this.resolutionForm.newTotalAmount = (this.disputeDetail.disputeAmount || 0) + (parseFloat(newAmount) || 0);
      }
    },

    // Reset financial fields when financial impact is disabled
    'resolutionForm.hasFinancialImpact'(hasImpact) {
      if (!hasImpact) {
        this.resolutionForm.adjustmentType = '';
        this.resolutionForm.adjustmentAmount = 0;
        this.resolutionForm.newTotalAmount = this.disputeDetail.disputeAmount;
        this.resolutionForm.approvalLevel = 'NONE';
      }
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.form-check-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.form-check-group .form-check {
  margin-bottom: 0.5rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.alert-heading {
  font-size: 1rem;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.modal-xl {
  max-width: 1200px;
}

.card-header.bg-light {
  background-color: #f8f9fa !important;
}

.border {
  border-color: #dee2e6 !important;
}
</style>